<template>
  <div class="auth-wrapper auth-v1">
    <div class="auth-inner">
      <v-card class="auth-card">
        <!-- Logo Section -->
        <v-card-title class="d-flex align-center justify-center py-7">
          <router-link to="/" class="d-flex align-center">
            <v-img 
              :src="require(`@/assets/images/logos/logo.png`)" 
              max-width="200px" 
              alt="logo" 
              contain 
              class="me-0"
            ></v-img>
          </router-link>
        </v-card-title>
 <v-card-text>
          <p 
            class="mb-2 text-center"
            :style="{ 
              color: messageType === 'error' ? 'red' : 'teal',
              fontWeight: 500
            }"
          >
            {{ snackBarMessage }}
          </p>
        </v-card-text>
        <!-- Dynamic Content Based on Step -->
        <template v-if="step === 1">
          <!-- Login Step -->
          <v-card-text>
            <p class="text-2xl font-weight-semibold text--primary mb-2">
              Welcome Back, Manager! 👔
            </p>
            <p class="mb-2">
              Please sign-in to your account and start the adventure
            </p>
          </v-card-text>

          <v-card-text>
            <v-form @submit.prevent="handleLogin">
              <v-text-field
                v-model="username"
                outlined
                label="Username"
                placeholder="JohnDoe"
                ref="username"
                class="mb-3"
                hide-details
              ></v-text-field>

              <v-text-field
                v-model="password"
                outlined
                :type="isPasswordVisible ? 'text' : 'password'"
                label="Password"
                placeholder="············"
                ref="password"
                :append-icon="isPasswordVisible ? icons.mdiEyeOffOutline : icons.mdiEyeOutline"
                hide-details
                @click:append="isPasswordVisible = !isPasswordVisible"
              ></v-text-field>

              <div class="d-flex align-center justify-space-between flex-wrap mt-4">
                <v-checkbox
                  label="Remember Me"
                  hide-details
                  class="me-3 mt-1"
                ></v-checkbox>
              </div>

              <spinner v-if="isLoading"></spinner>
              <v-btn
                v-else
                block
                color="primary"
                class="mt-6"
                type="submit"
              >
                Continue to Security Check
              </v-btn>
            </v-form>
          </v-card-text>
        </template>

        <!-- OTP Verification Step -->
        <template v-else-if="step === 2">
          <v-card-text>
            <p class="text-2xl font-weight-semibold text--primary mb-2">
              Secure Identity Verification 🔐
            </p>
            <p class="mb-4">
              We've sent a 6-digit code to your registered email address
            </p>
          </v-card-text>

          <v-card-text>
            <v-form @submit.prevent="verifyOtp">
              <v-text-field
                v-model="otp"
                outlined
                label="Verification Code"
                placeholder="123456"
                class="mb-4"
                :rules="[v => !!v || 'Code is required']"
                hide-details
              ></v-text-field>

              <div class="text-center mb-4">
                <a
                  :href="`https://mail.google.com/mail/u/${email}/`"
                  target="_blank"
                  class="gmail-link"
                >
                  <v-icon color="#EA4335">mdi-gmail</v-icon>
                  Open Gmail to retrieve code
                </a>
              </div>

              <spinner v-if="isVerifying"></spinner>
              <v-btn
                v-else
                block
                color="primary"
                type="submit"
              >
                Verify Identity
              </v-btn>
            </v-form>

            <div class="text-center mt-4">
              <v-btn
                text
                small
                @click="resendOtp"
                :disabled="resendCooldown > 0"
                color="primary"
              >
                <v-icon left>mdi-reload</v-icon>
                Resend Code {{ resendCooldown > 0 ? `(${resendCooldown}s)` : '' }}
              </v-btn>
            </div>
          </v-card-text>
        </template>

        <!-- Status Messages -->
       
      </v-card>
    </div>

    <!-- Background Elements -->
    <img 
      class="auth-mask-bg" 
      height="173" 
      :src="require(`@/assets/images/misc/mask-${$vuetify.theme.dark ? 'dark' : 'light'}.png`)"
    />

    <v-img
      class="auth-tree"
      width="247"
      height="185"
      src="@/assets/images/misc/tree.png"
    ></v-img>

    <v-img
      class="auth-tree-3"
      width="377"
      height="289"
      src="@/assets/images/misc/tree-3.png"
    ></v-img>
  </div>
</template>
<script>
import axios from 'axios'
import store from "../../store";
import { mdiEyeOutline, mdiEyeOffOutline } from "@mdi/js";
import Spinner from "@/components/Spinner.vue";

export default {
  components: { Spinner },
data() {
    return {
      // Login Step Data
      username: '',
      password: '',
      isPasswordVisible: false,
      
      // OTP Step Data
      step: 1,
      otp: '',
      tempToken: null,
      email: '',
      resendCooldown: 0,
      isVerifying: false,
      
      // Icons
      icons: {
        mdiEyeOutline,
        mdiEyeOffOutline
      }
    }
  },
  computed: {
    snackBarMessage() {
      return store.state.snackBarMessage
    },
    isLoading() {
      return store.state.isLoading
    },
    messageType() {
      return store.state.snackBarColor.includes('red') ? 'error' : 'success'
    },
    authHeaders() {
      return { Authorization: `Token ${this.tempToken}` }
    }
  },
  mounted() {
    document.title = "PettyCash: Manager | Log In"
    this.checkExistingSession()
  },
  methods: {
    // Initialization Methods
    checkExistingSession() {
      if (this.$store.state.isAuthenticated) {
        this.showMessage("You are already logged in.", 'red')
        this.$router.push({ name: 'savings.dashboard' })
      }
    },

    // Core Authentication Flow
    async handleLogin() {
      try {
        if (this.username.length < 4) {
          this.showMessage("User name is too short, needs to be at least 4 characters", "red");
          this.$refs.username.focus();
          return;
        } else if (this.password.length < 8) {
          this.showMessage("Password is too short, needs to be at least 8 characters", "red");
          this.$refs.password.focus();
          return;
        }
        this.clearAuthHeaders()
        
        const response = await axios.post('/api/v1/token/login/', {
          username: this.username,
          password: this.password
        })

        this.tempToken = response.data.auth_token
        await this.handleManagerVerification()
        
      } catch (error) {
        this.handleAuthError(error, 
          "An error occurred while logging into your account",
          true // Préserve le comportement original
        )
      }
    },

    async handleManagerVerification() {
      try {
        const { data } = await axios.get('/api/v1/manager/check-manager/', {
          headers: this.authHeaders
        })

        if (data.is_manager) {
          this.step = 2
          this.email = data.email
          await this.sendOtp()
          this.showMessage('🔒 Security code sent to your registered email!', 'teal')
        } else {
          this.completeLogin(response.data.auth_token)
        }
        
      } catch (error) {
        this.handleAuthError(error)
        this.showMessage('👤 Manager access only. Please contact us – we couldn’t verify your status.', 'orange');

      }
    },

    // OTP Management
    async sendOtp() {
      try {
        await axios.post('/api/v1/manager/send-manager-otp/', {}, {
          headers: this.authHeaders
        })
        this.startResendCooldown()
      } catch (error) {
        this.showMessage('⚠️ Failed to send verification code. Please try again.', 'red')
      }
    },

    async verifyOtp() {
      this.isVerifying = true
      try {
        await axios.post('/api/v1/manager/verify-manager-otp/', 
          { otp: this.otp }, 
          { headers: this.authHeaders }
        )
        this.completeLogin(this.tempToken)
        this.showMessage('🎉 Identity successfully verified!', 'teal')
      } catch (error) {
        this.handleOtpError(error)
      } finally {
        this.isVerifying = false
      }
    },

    // Helpers
    startResendCooldown() {
      this.resendCooldown = 30
      const timer = setInterval(() => {
        this.resendCooldown > 0 ? this.resendCooldown-- : clearInterval(timer)
      }, 1000)
    },

    completeLogin(token) {
      this.$store.commit('setToken', token)

  axios.defaults.headers.common['Authorization'] = "Token " + token
  
 
  localStorage.setItem('token', token)
  

  this.$store.state.username = this.username
  

  this.$router.push({ name: 'savings.dashboard' })
  

  this.showMessage(`Welcome back ${this.username}! 😃`, 'teal')
},
 

    showMessage(text, color) {
      store.commit('setSnackBarMessage', text)
      store.commit('setSnackBarColor', `${color} darken-4`)
      store.commit('activateSnackBar', true)
    },

    // Error Handling
    handleAuthError(error, defaultMsg, isLoginError) {
      if (error.response?.data) {
        let errorMessage = ''
        
        // Traitement spécifique pour les erreurs de login
        if (isLoginError) {
          const errors = []
          for (const property in error.response.data) {
            if (Array.isArray(error.response.data[property])) {
              errors.push(...error.response.data[property])
            } else {
              errors.push(error.response.data[property])
            }
          }
          errorMessage = errors.map(e => `• ${e}`).join('\n')
        } else {
          errorMessage = error.response.data.error || defaultMsg
        }
        
        this.showMessage(`⚠️ ${errorMessage}`, 'red')
      } else {
        this.showMessage(`⚠️ ${defaultMsg}`, 'red')
      }
    },

    handleOtpError(error) {
      const message = error.response?.data?.error || 
                     'Invalid or expired verification code'
      this.showMessage(`⚠️ ${message}`, 'red')
    },

    clearAuthHeaders() {
      axios.defaults.headers.common['Authorization'] = ""
      localStorage.removeItem('token')
    },

    // Resend OTP Handler
    async resendOtp() {
      if (this.resendCooldown > 0) return
      await this.sendOtp()
      this.showMessage('🔄 New code sent to your email!', 'teal')
    }
  },

}
</script>

<style lang="scss">
  @import "~@/plugins/vuetify/default-preset/preset/pages/auth.scss";
</style>